import type { DeviceModel } from '@/types/production/device';

/**
 * Maps product names to Device.Model enum values
 * This mapping is based on the product names used in production
 */
export function mapProductNameToDeviceModel(productName: string): DeviceModel | null {
  const normalizedName = productName.toLowerCase().trim();

  // Direct mappings for known product names
  const mappings: Record<string, DeviceModel> = {
    // Rastreador variants
    'rastreador bws nb2': 'DM_BWS_NB2',
    'rastreador e3+': 'DM_E3_PLUS',

    // SimCARD (treated as a device for registration purposes)
    'simcard emnify': 'UNRECOGNIZED',

    // E3 variants
    'e3+': 'DM_E3_PLUS',
    'e3+ personal': 'DM_E3_PLUS_PERSONAL',
    'e3+personal': 'DM_E3_PLUS_PERSONAL',
    'e3+ pet': 'DM_E3_PLUS_PET',
    'e3+pet': 'DM_E3_PLUS_PET',
    'e3+ obd': 'DM_E3_PLUS_OBD_II',
    'e3+obd': 'DM_E3_PLUS_OBD_II',
    'e3+ long life': 'DM_E3_PLUS_LONG_LIFE',
    'e3+ long_life': 'DM_E3_PLUS_LONG_LIFE',
    'e3+long life': 'DM_E3_PLUS_LONG_LIFE',
    'e3+long_life': 'DM_E3_PLUS_LONG_LIFE',
    'e3+ ll + redes': 'DM_E3_PLUS_LONG_LIFE',
    'e3+ ll + rf': 'DM_E3_PLUS_LONG_LIFE',
    'e3+s': 'DM_E3_PLUS', // E3+S maps to E3+
    e3: 'DM_E3',
    'e3+ 4g': 'DM_E3_PLUS_4G',
    'e3+4g': 'DM_E3_PLUS_4G',

    // BWS variants
    'bws nb2': 'DM_BWS_NB2',
    'bws lora': 'DM_BWS_LORA',
    'bws nb2 + lora': 'DM_BWS_NB2_LORA',
    'bws nb2lora': 'DM_BWS_NB2_LORA',
    'bws 4g lora': 'DM_BWS_PLUS_4G_LORA',
    'bws 4g+lora': 'DM_BWS_PLUS_4G_LORA',
    'bws 4g': 'DM_BWS_4G',
    'bws ll nb': 'DM_BWS_LL_NB',
    'bws ll nb iot+lora': 'DM_BWS_LL_NB_LORA',
    'bws ll nb iot + lora': 'DM_BWS_LL_NB_LORA',
    'bws ll nb+lora': 'DM_BWS_LL_NB_LORA',
    'bws ll nb + lora': 'DM_BWS_LL_NB_LORA',
    'bws bait nb': 'DM_BWS_BAIT_NB',
    'bws bait nb iot+lora': 'DM_BWS_BAIT_NB_LORA',
    'bws bait nb iot + lora': 'DM_BWS_BAIT_NB_LORA',
    'bws bait nb+lora': 'DM_BWS_BAIT_NB_LORA',
    'bws bait nb + lora': 'DM_BWS_BAIT_NB_LORA',

    // Other variants
    gt06: 'DM_GT06',
  };

  return mappings[normalizedName] || null;
}

/**
 * Checks if a product name can be mapped to a device model
 * Used to filter products that are actual devices (not accessories)
 */
export function isDeviceProduct(productName: string): boolean {
  return mapProductNameToDeviceModel(productName) !== null;
}
